import React, { ReactNode, useCallback, useRef, useState } from 'react';

import { isApolloError } from '@apollo/client';
import { Button } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { Nullable } from '@rbi-ctg/frontend';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { IConfirmProps } from 'components/confirm-dialog';
import Dialog from 'components/dialog';
import { IDialogProps } from 'components/dialog/types';
import Link from 'components/link';
import { DialogCb, IUseDialogComponentProps, IUseDialogProps } from 'hooks/use-dialog-modal';
import { IGraphqlErrorMap, createGqlErrorMap, parseGraphQLErrorCodes } from 'utils/errors';
import logger from 'utils/logger';
import noop from 'utils/noop';

import useRoutes from './use-routes';

export type ModalCb = (data: IErrorModal) => void;

interface IUseErrorModalArgs<T> {
  Component?: React.ComponentType<React.PropsWithChildren<IUseDialogComponentProps>>;
  onConfirm?: DialogCb<T>;
  onCancel?: DialogCb<T>;
  onDismiss?: DialogCb<T>;
  onOpen?: ModalCb;
  type?: string;
  showCancel?: boolean;
  init?: boolean;
  allowDismiss?: boolean;
  modalAppearanceEventMessage: string;
}

export interface IErrorModal {
  body?: string | ReactNode;
  link?: {
    key: string;
    to: string;
    text: string;
  };
  message?: ReactNode;
  error?: Error;
  name?: string;
  title?: string | JSX.Element;
  modalAppearanceEventMessage?: string;
  confirmationText?: string;
  dismissText?: string;
  onConfirm?: Function;
  onDismiss?: Function;
  isPspError?: boolean;
  dialogProps?: Partial<IDialogProps>;
}

type Props<P> = IUseDialogProps & Partial<IDialogProps> & Partial<IConfirmProps> & P;
export type UseErrorDialogHook<P = any> = [
  React.FC<React.PropsWithChildren<Props<P>>>,
  ModalCb,
  () => void
];

export default function useErrorModal<T extends object, P = {}>({
  Component,
  onConfirm = noop,
  onCancel = noop,
  onDismiss = onCancel,
  onOpen = noop,
  init = false,
  allowDismiss = true,
  /**
   * A small string sent to amplitude describing the purpose of the modal.
   */
  modalAppearanceEventMessage,
}: IUseErrorModalArgs<T>): UseErrorDialogHook<P> {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(init);
  const { getLocalizedRouteForPath } = useRoutes();
  const [pendingData, setPending] = useState<Nullable<T>>(null);
  const modalAppearanceEventMessageOverride = useRef<string | null>(null);
  const errorEventMessageOverride = useRef<string | null>(null);
  const openErrorDialog = useCallback(
    (data: IErrorModal) => {
      onOpen(data);

      if (data) {
        const { error, modalAppearanceEventMessage: eventMessageOverride } = data;
        const message: string | null = typeof data.message === 'string' ? data.message : null;
        let parsedError: Error | IGraphqlErrorMap | undefined = error;

        if (eventMessageOverride) {
          modalAppearanceEventMessageOverride.current = eventMessageOverride;
        }

        if (parsedError && isApolloError(parsedError)) {
          const errors = parseGraphQLErrorCodes(parsedError);

          if (errors.length) {
            const [{ errorCode, message: errorMessage }] = errors;
            errorEventMessageOverride.current = [errorCode, errorMessage].join(' ');
            // we cannot filter datadog results based on array elements
            // so we create a map of the list of errors supplied by apollo
            parsedError = createGqlErrorMap([...parsedError.graphQLErrors]);
          }
        }

        logger.error({
          error: parsedError,
          message: eventMessageOverride || modalAppearanceEventMessage,
          modalHeader: eventMessageOverride || modalAppearanceEventMessage,
          modalMessage: message,
          isErrorModal: true,
        });
      }

      setPending(data as T);
      setOpen(true);
    },
    [modalAppearanceEventMessage, onOpen]
  );

  const dismissDialog = useCallback(() => {
    onDismiss(pendingData);
    if (allowDismiss) {
      setOpen(false);
    }
    setPending(null);
  }, [allowDismiss, onDismiss, pendingData]);

  const confirmDialog = useCallback(() => {
    onConfirm(pendingData);
    setOpen(false);
    setPending(null);
    onCancel();
  }, [onCancel, onConfirm, pendingData]);

  const ErrorDialogComponent: UseErrorDialogHook<P>[0] = useCallback(
    // @ts-ignore TS(2590) FIXME: Expression produces a union type that is too complex to represent.
    ({
      buttonLabel = formatMessage({ id: 'okay' }),
      heading = formatMessage({ id: 'somethingWrong' }),
      image,
      ...rest
    }) => {
      if (!open) {
        return null;
      }

      const msg = pendingData && 'message' in pendingData ? pendingData.message : null;
      const title = pendingData && 'title' in pendingData ? pendingData.title : null;
      const body = pendingData && 'body' in pendingData ? pendingData.body : null;
      // FIXME: fix all any types
      const link: any = pendingData && 'link' in pendingData ? pendingData.link : null;
      const confirmationText: any =
        pendingData && 'confirmationText' in pendingData ? pendingData.confirmationText : null;
      const dismissText: any =
        pendingData && 'dismissText' in pendingData ? pendingData.dismissText : null;
      const onConfirmAction: any =
        pendingData && 'onConfirm' in pendingData ? pendingData.onConfirm : null;
      const onDismissAction: any =
        pendingData && 'onDismiss' in pendingData ? pendingData.onDismiss : null;
      const isPspError: any =
        pendingData && 'isPspError' in pendingData ? pendingData.isPspError : false;
      const dialogProps: any =
        pendingData && 'dialogProps' in pendingData ? pendingData.dialogProps : null;

      let linkBody;
      if (Component) {
        return <Component onDismiss={dismissDialog} onConfirm={confirmDialog} />;
      }

      if (link) {
        linkBody = formatMessage(
          { id: `${link.key}` },
          {
            link: (
              <Link to={getLocalizedRouteForPath(link.to)} onPress={dismissDialog}>
                {link.text}
              </Link>
            ),
          }
        );
      }

      const dialogBody = linkBody || body || msg || formatMessage({ id: 'errorProcessingRequest' });
      const actions =
        confirmationText && dismissText && onConfirmAction && onDismissAction ? (
          <Button.Group marginTop="$4">
            <ActionButton
              fullWidth
              onPress={onConfirmAction}
              testID="error-dialog-confirm-btn"
              mb={2}
            >
              {confirmationText}
            </ActionButton>
            <ActionButton
              fullWidth
              onPress={onDismissAction}
              testID="error-dialog-dismiss-btn"
              variant={ActionButtonVariants.TEXT_ONLY}
            >
              {dismissText || buttonLabel}
            </ActionButton>
          </Button.Group>
        ) : (
          <ActionButton
            fullWidth
            marginTop="$4"
            onPress={onConfirmAction || confirmDialog}
            testID="error-dialog-confirm-btn"
          >
            {dismissText || buttonLabel}
          </ActionButton>
        );

      return (
        // @ts-ignore TS(2590) FIXME: Expression produces a union type that is too complex to represent.
        <Dialog
          showDialog={open}
          heading={!isPspError ? title || heading : null}
          bodyComponent={dialogBody}
          body={typeof dialogBody === 'string' ? dialogBody : undefined}
          image={image}
          onDismiss={dismissDialog}
          actions={actions}
          /* don't log again because we have already logged */
          shouldLogToDdWhenIsErrorModal={false}
          isErrorModal
          modalAppearanceEventMessage={
            modalAppearanceEventMessageOverride.current || modalAppearanceEventMessage
          }
          errorEventMessage={errorEventMessageOverride.current ?? undefined}
          {...rest}
          {...dialogProps}
        />
      );
    },
    [
      formatMessage,
      open,
      pendingData,
      Component,
      confirmDialog,
      dismissDialog,
      modalAppearanceEventMessage,
      getLocalizedRouteForPath,
    ]
  );

  return [ErrorDialogComponent, openErrorDialog, dismissDialog];
}
